import React, { useState, useEffect } from 'react';
import { Box, CardMedia, Container, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Divider } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { primaryColor } from '../theme';
import '../App.css';
import { ArrowBack } from '@mui/icons-material';
import moment from 'moment';

const ContentDeletionRequest = () => {
  const { contentId, deletionRequestId } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/content/${contentId}/deletion-request/${deletionRequestId}`);
        setContent(response.data.content);
        setComment(response.data.deletionRequest.comment || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching content:', error);
        setLoading(false);
      }
    };

    fetchContent();
  }, [contentId, deletionRequestId]);

  const handleDeleteOpen = () => {
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const userId = user ? user.id : null;
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/content/${contentId}/confirm-deletion/${deletionRequestId}`);
      setDeleteOpen(false);
      navigate('/approver/dashboard'); // Navigate back to the dashboard
    } catch (error) {
      console.error('Error deleting content:', error);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!content) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h6" color="error">Content not found</Typography>
      </Container>
    );
  }

  const getPresentationEmbedUrl = (url) => {
    if (url.endsWith('.pptx')) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    }
    return url;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/approver/dashboard')} sx={{ mb: 2 }}>
        Go to Dashboard
      </Button>
      <Typography variant="h4" gutterBottom style={{ color: primaryColor }}>
        Content Details
      </Typography>
      <Typography variant="h6" gutterBottom>{content.topic}</Typography>
      <Typography variant="subtitle1" gutterBottom>Target User: {content.targetUser}</Typography>
      <Typography variant="subtitle1" gutterBottom>Value Chain: {content.valueChain}</Typography>
      <Typography variant="subtitle1" gutterBottom>Platform: {content.platform}</Typography>
      {content.video && (
        <Box mt={2}>
          <CardMedia
            component="video"
            controls
            height="200"
            src={content.video}
            title={content.topic}
          />
        </Box>
      )}
      {content.audio && (
        <Box mt={2}>
          <audio controls>
            <source src={content.audio} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        </Box>
      )}
      {content.presentation && (
        <Box mt={2}>
          <iframe
            src={getPresentationEmbedUrl(content.presentation)}
            width="100%"
            height="400"
            title={content.topic}
            frameBorder="0"
            allowFullScreen
          />
        </Box>
      )}
      <Typography variant="body1" dangerouslySetInnerHTML={{ __html: content.content }} />
      <Divider sx={{ my: 4 }} />
      <Typography variant="body1" gutterBottom>Deletion Reason: {comment}</Typography>
      <Box mt={2} display="flex" justifyContent="space-between">
        <Button variant="contained" color="error" onClick={handleDeleteOpen}>
          Confirm Delete
        </Button>
      </Box>

      <Dialog open={deleteOpen} onClose={handleDeleteClose}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this content?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContentDeletionRequest;